import {
  FilterBody,
  Pagination,
  SortOption,
  YesObject,
  usePagination
} from '@yes.technology/react-toolkit'
import { useCallback, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { merge } from 'lodash'
import api from 'utils/api'
import { FetchYesObjectsResponse } from 'utils/apiTypes'
import {
  fetchFilteraction,
  resolveQuery
} from 'modules/imported/FilteractionResolver'

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>

type SerializibleKey = string | { [x: string]: string }

type UseFetchYesObjectsArgs = {
  initialPagination?: Partial<Pagination>
  initialSortOptions?: SortOption[]
  filterBody?: Optional<
    FilterBody,
    'pagination' | 'orderby' | 'skip_metadata' | 'project_fields'
  >
  showLoading?: boolean
  filteractionUuid?: string
  enabled?: boolean
  interpolationObject?: Record<string, unknown>
  shouldMergeFilters?: boolean
  additionalQueryKeyParams?: SerializibleKey[]
  refetchInterval?: number
}

export const INITIAL_PAGINATION = { limit: 10, offset: 0 }

export const INITIAL_SORT_OPTIONS: SortOption[] = [
  {
    date_creation_row: 'desc'
  }
]

const emptyArr: YesObject[] = []

export default function useFetchYesObjects<T extends YesObject>({
  initialPagination = INITIAL_PAGINATION,
  initialSortOptions = INITIAL_SORT_OPTIONS,
  filterBody,
  filteractionUuid,
  showLoading = false,
  enabled = true,
  shouldMergeFilters = false,
  additionalQueryKeyParams = [],
  refetchInterval
}: UseFetchYesObjectsArgs) {
  const { pagination, setPage } = usePagination({
    initialPagination
  })

  const [sortOptions, setSortOptions] =
    useState<SortOption[]>(initialSortOptions)

  const { data, error, isLoading, isFetching, refetch, isFetched } = useQuery<
    FetchYesObjectsResponse,
    Error
  >({
    queryKey: [
      'YesObjects',
      pagination.limit,
      pagination.offset,
      { filterBody, showLoading, sortOptions },
      filteractionUuid,
      ...additionalQueryKeyParams
    ],
    queryFn: async () => {
      if (!filterBody && !filteractionUuid)
        return Promise.reject(
          new Error('Either filterBody or filteractionUuid must be provided.')
        )

      let callingFilterBody = {
        ...filterBody,
        pagination,
        orderby: sortOptions
      } as FilterBody

      if (filteractionUuid) {
        const filteraction = await fetchFilteraction(
          filteractionUuid,
          showLoading
        )

        const filteractionFilterBody = await resolveQuery(filteraction?.query)

        const mergedFilterQuery = {
          $and: [
            {
              ...merge(filteractionFilterBody ?? {}, filterBody?.query ?? {})
            }
          ]
        }

        const finalQuery = shouldMergeFilters
          ? mergedFilterQuery
          : filteractionFilterBody

        callingFilterBody = {
          ...filteractionFilterBody,
          query: finalQuery,
          skip_metadata: filterBody?.skip_metadata,
          skip_relations: filterBody?.skip_relations,
          project_fields: filterBody?.project_fields
        }
      }

      return api.postObjectsFilter(callingFilterBody, showLoading, false)
    },
    enabled,
    refetchInterval
  })

  const onSetSortOptions = useCallback(
    (sortOptions: SortOption[]) => {
      if (sortOptions?.length > 0) {
        const [sortOptionKey] = sortOptions
        const [sortOptionValue] = Object.values(sortOptionKey)

        setSortOptions(sortOptionValue ? sortOptions : initialSortOptions)
      }
    },
    [initialSortOptions]
  )

  return {
    yesObjects: (data?.objects || emptyArr) as T[],
    metadata: data?.metadata,
    isLoading,
    isFetching,
    isFetched,
    fetchYesObjects: refetch,
    pagination: data?.pagination || pagination,
    error,
    totalObjects: data?.pagination?.total || 0,
    setPage,
    sortOptions,
    setSortOptions: onSetSortOptions
  }
}
